import React, { useMemo } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import { useClusterRCFAnalysisStore } from 'store/clusterRCFAnalysis.store';
import { useRiskRegisterStore } from 'store/riskRegister.store';
import { useThemeStatusStore } from 'store/themeStatus.store';

import { QueryResponse } from 'api/queries/clustersData.query';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { LineChart } from 'components/common/charts/lineChart/lineChart';

import { Select } from 'components/DesignSystem/Select';
import classes from 'components/pages/DurationForecastPage/components/GraphComponents/components/RCF/rcf.module.scss';
import {
    generateLinkToCreateRisk,
    getValueFromData,
    serializeData,
    serializeOptions,
} from 'components/pages/DurationForecastPage/components/GraphComponents/components/RCF/rcf.utils';

interface Props {
    rcfDurationData?: QueryResponse['rcf']['duration'];
    rcfDurationRatioData?: QueryResponse['rcf']['ratio'];
    tooltipArg?: IToolTip;
    loadingCluster: boolean;
    subTitle: string;
}

type TDurationRatio = 'Duration' | 'DurationRatio';

const Rcf = ({ rcfDurationData, rcfDurationRatioData, tooltipArg, loadingCluster, subTitle }: Props) => {
    const { themeStatus } = useThemeStatusStore();
    const history = useHistory();
    const { update } = useRiskRegisterStore();
    const { id } = useParams<{ id: string }>();
    const selectedCluster = useClusterRCFAnalysisStore((store) => store.selectedClusterId);

    // we need to plot both duration in days and durationRatio
    const [durationOrDurationRatio, setDurationOrDurationRatio] = React.useState<ISelectOption<TDurationRatio>>({
        label: ' Duration Ratio',
        value: 'DurationRatio',
    });

    const data = useMemo(() => {
        if (rcfDurationData && rcfDurationRatioData) {
            return durationOrDurationRatio.value === 'Duration' ? rcfDurationData : rcfDurationRatioData;
        }
        return [];
    }, [durationOrDurationRatio, rcfDurationData, rcfDurationRatioData]);

    const series = serializeData({ data: data, themeStatus, yaxisName: durationOrDurationRatio.label });
    const graphOptions = serializeOptions({
        data: data,
        themeStatus,
        yaxisName: durationOrDurationRatio.label,
        isAnnotation: durationOrDurationRatio.value === 'DurationRatio',
    });

    const title = 'Cluster RCF';
    const options: ISelectOption<TDurationRatio>[] = [
        { label: 'Duration (days)', value: 'Duration' },
        { label: ' Duration Ratio', value: 'DurationRatio' },
    ];
    const handleChange = (value: ISelectOption<TDurationRatio> | null) => {
        if (value) {
            setDurationOrDurationRatio(value);
        }
    };

    const p50 = getValueFromData({ data, target: 50 });
    const p80 = getValueFromData({ data, target: 80 });

    const createRiskRegister = () => {
        update({ activeTab: 'table' });
        const linkToCreateRisk = generateLinkToCreateRisk({
            id,
            data,
            durationOrDurationRatio,
            selectedCluster,
        });
        history.push(linkToCreateRisk);
    };

    return (
        <WidgetWithTitle
            title={<WidgetTitle title={title} />}
            tooltip={tooltipArg}
            titleComponents={[
                <Select<ISelectOption<TDurationRatio>>
                    options={options}
                    id={'duration-select'}
                    onChange={handleChange}
                    value={durationOrDurationRatio}
                    key={'durationRatio'}
                />,

                <ExportDropdown title={title} key={'export'} subTitle={subTitle} />,
            ]}
            className={classes.RCFWidget}
        >
            <div className={classes.boxContainer}>
                <div className={classes.boxWrapper}>
                    {!loadingCluster && data.length > 0 && (
                        <LineChart
                            series={series}
                            options={graphOptions}
                            noData={false}
                            isFetching={false}
                            height={430}
                        />
                    )}
                    {!loadingCluster && data.length === 0 && (
                        <p className={classes.text}>No actual (completed) data exists</p>
                    )}
                    {loadingCluster && <p className={classes.text}>Waiting for data...</p>}
                    <div data-testid={'duration-ratio'} className={classes.durationRatio}>
                        <div>
                            {durationOrDurationRatio.label}: <span className={classes.title}>Typical:</span>{' '}
                            <span className={classes.value}>{p50 ? Math.round(p50.value * 100) / 100 : ''}</span>,{' '}
                        </div>
                        <div>
                            <span className={classes.title}>Pessimistic:</span>{' '}
                            <span className={classes.value}>{p80 ? Math.round(p80.value * 100) / 100 : ''}</span>
                        </div>
                        {selectedCluster && (
                            <div className={classes.linkToCreateRisk}>
                                <span className={classes.link} onClick={createRiskRegister}>
                                    Create Corresponding Risk
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </WidgetWithTitle>
    );
};

export default Rcf;
